import { useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { Phone, Send, Loader2, CheckCircle2 } from "lucide-react";
import { CONTACT } from "@/lib/data";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SERVICE_OPTIONS = [
  "Business Waste Service",
  "Roll-Off Dumpster Service",
  "Recycling",
  "Other / Not Sure",
];

const SERVICE_CONFIG = {
  "Business Waste Service": {
    sizes: ["1 Yard", "2 Yard", "3 Yard", "4 Yard", "6 Yard", "8 Yard"],
    frequencies: [
      "1x per week", "2x per week", "3x per week", "4x per week",
      "5x per week", "6x per week", "7x per week",
    ],
    sizeLabel: "Container Size",
  },
  "Roll-Off Dumpster Service": {
    sizes: ["10 Yard", "20 Yard", "30 Yard", "40 Yard"],
    frequencies: ["On-Call", "1x per week", "2x per week", "Bi-Weekly", "1x per month"],
    sizeLabel: "Dumpster Size",
  },
};

const initial = { name: "", business: "", email: "", phone: "", service: "", container_size: "", frequency: "", quantity: "", message: "" };

export default function QuoteForm() {
  const [form, setForm] = useState(initial);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const onServiceChange = (e) => {
    setForm({ ...form, service: e.target.value, container_size: "", frequency: "", quantity: "" });
  };

  const cfg = SERVICE_CONFIG[form.service];

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.phone || !form.service) {
      toast.error("Please fill in your name, email, phone and service needed.");
      return;
    }
    setLoading(true);
    try {
      const payload = { ...form, quantity: form.quantity ? Number(form.quantity) : null };
      await axios.post(`${API}/quote`, payload);
      toast.success("Thanks for submitting! Our account managers will be in touch shortly.");
      setDone(true);
      setForm(initial);
    } catch (err) {
      toast.error("Something went wrong. Please call us at " + CONTACT.phone);
    } finally {
      setLoading(false);
    }
  };

  const inputCls =
    "w-full px-4 py-3.5 rounded-xl bg-[#FBFAF8] border border-slate-200 text-[#0B0F14] placeholder:text-slate-400 focus:outline-none focus:border-emerald-600/60 transition-colors text-sm";

  return (
    <section id="quote" className="relative bg-[#F0EFEA] py-24 lg:py-32">
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <div className="grid lg:grid-cols-[0.85fr_1.15fr] gap-12 lg:gap-16 items-start">
          {/* Left copy */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
          >
            <span className="font-mono-tech text-xs uppercase tracking-[0.25em] text-emerald-600 font-semibold">
              / 06 — Contact
            </span>
            <h2 className="mt-4 font-display font-extrabold uppercase tracking-tight text-3xl sm:text-4xl lg:text-5xl text-[#0B0F14] leading-[1.05]">
              Need Help? Talk To One Of Our Account Managers Today
            </h2>
            <p className="mt-6 text-slate-600 leading-relaxed text-sm sm:text-base max-w-md">
              Get the best prices for waste management services in Miami. Request a free quote and
              start managing your waste efficiently and cost-effectively.
            </p>

            <a
              href={CONTACT.phoneHref}
              className="mt-8 inline-flex items-center gap-4 p-5 rounded-2xl bg-[#FBFAF8] border border-slate-200 hover:border-emerald-600/50 transition-colors group w-full sm:w-auto shadow-soft"
              data-testid="phone-cta-quote"
            >
              <span className="grid place-items-center w-12 h-12 rounded-xl bg-emerald-600 text-white group-hover:scale-105 transition-transform">
                <Phone size={22} />
              </span>
              <span>
                <span className="block font-mono-tech text-[11px] uppercase tracking-widest text-slate-500">
                  Call Us Today
                </span>
                <span className="block font-display font-bold text-xl text-[#0B0F14]">{CONTACT.phone}</span>
              </span>
            </a>
          </motion.div>

          {/* Form */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7, delay: 0.1 }}
            className="bg-[#FBFAF8] border border-slate-200 rounded-3xl p-6 sm:p-10 shadow-soft"
          >
            {done ? (
              <div className="py-16 text-center" data-testid="quote-success">
                <CheckCircle2 size={56} className="text-emerald-600 mx-auto mb-6" />
                <h3 className="font-display font-bold text-2xl text-[#0B0F14] mb-3">Thanks for submitting!</h3>
                <p className="text-slate-600 text-sm max-w-sm mx-auto">
                  One of our account managers will reach out shortly. Need immediate help? Call{" "}
                  <a href={CONTACT.phoneHref} className="text-emerald-600 font-semibold">{CONTACT.phone}</a>.
                </p>
                <button
                  onClick={() => setDone(false)}
                  className="mt-8 px-6 py-3 rounded-full border border-slate-300 text-[#0B0F14] text-sm font-semibold hover:border-emerald-600 transition-colors"
                  data-testid="quote-reset-btn"
                >
                  Submit Another Request
                </button>
              </div>
            ) : (
              <form onSubmit={submit} className="space-y-4" data-testid="quote-form">
                <div className="grid sm:grid-cols-2 gap-4">
                  <input data-testid="quote-name-input" required autoComplete="name" aria-label="Full Name" className={inputCls} placeholder="Full Name *" value={form.name} onChange={update("name")} />
                  <input data-testid="quote-business-input" autoComplete="organization" aria-label="Business Name" className={inputCls} placeholder="Business Name" value={form.business} onChange={update("business")} />
                </div>
                <div className="grid sm:grid-cols-2 gap-4">
                  <input data-testid="quote-email-input" required type="email" autoComplete="email" aria-label="Email Address" className={inputCls} placeholder="Email Address *" value={form.email} onChange={update("email")} />
                  <input data-testid="quote-phone-input" required type="tel" autoComplete="tel" aria-label="Phone Number" className={inputCls} placeholder="Phone Number *" value={form.phone} onChange={update("phone")} />
                </div>
                <select data-testid="quote-service-select" required aria-label="Service Needed" className={`${inputCls} ${form.service ? "text-[#0B0F14]" : "text-slate-400"}`} value={form.service} onChange={onServiceChange}>
                  <option value="">Service Needed *</option>
                  {SERVICE_OPTIONS.map((o) => <option key={o} value={o} className="text-[#0B0F14]">{o}</option>)}
                </select>
                {cfg && (
                  <div className="grid sm:grid-cols-3 gap-4">
                    <select data-testid="quote-size-select" className={`${inputCls} ${form.container_size ? "text-[#0B0F14]" : "text-slate-400"}`} value={form.container_size} onChange={update("container_size")}>
                      <option value="">{cfg.sizeLabel}</option>
                      {cfg.sizes.map((s) => <option key={s} value={s} className="text-[#0B0F14]">{s}</option>)}
                    </select>
                    <select data-testid="quote-frequency-select" className={`${inputCls} ${form.frequency ? "text-[#0B0F14]" : "text-slate-400"}`} value={form.frequency} onChange={update("frequency")}>
                      <option value="">Frequency</option>
                      {cfg.frequencies.map((f) => <option key={f} value={f} className="text-[#0B0F14]">{f}</option>)}
                    </select>
                    <select data-testid="quote-quantity-select" className={`${inputCls} ${form.quantity ? "text-[#0B0F14]" : "text-slate-400"}`} value={form.quantity} onChange={update("quantity")}>
                      <option value="">Quantity</option>
                      {Array.from({ length: 10 }, (_, i) => i + 1).map((n) => <option key={n} value={n} className="text-[#0B0F14]">{n} container{n > 1 ? "s" : ""}</option>)}
                    </select>
                  </div>
                )}
                <textarea data-testid="quote-message-input" aria-label="Message or Job Details" rows={4} className={`${inputCls} resize-none`} placeholder="Message / Job Details" value={form.message} onChange={update("message")} />

                <button
                  type="submit"
                  disabled={loading}
                  data-testid="quote-submit-btn"
                  className="w-full flex items-center justify-center gap-2.5 px-6 py-4 rounded-xl bg-[#0E3B3B] text-white font-bold hover:bg-[#0A2C2C] active:scale-[0.99] transition-all disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {loading ? (
                    <><Loader2 size={18} className="animate-spin" /> Submitting…</>
                  ) : (
                    <><Send size={18} /> Submit Request</>
                  )}
                </button>
                <p className="text-center font-mono-tech text-[11px] text-slate-500 uppercase tracking-widest">
                  We&apos;ll beat or match our competition&apos;s pricing
                </p>
              </form>
            )}
          </motion.div>
        </div>
      </div>
    </section>
  );
}
